import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import MainHeader from "../components/MainHeader";
import { HiSearch } from "react-icons/hi";
import AdminSideBar from "../components/AdminSideBar";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useContent } from "../../pages/ContentContext";
import { uploadToCloudinary } from '../../utils/CloudinaryUpload';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TopBannerMainPage = () => {
  // Initial content state
  const initialContent = {
    title: "AGRITECH 101: HOW A SMART SCHOOL GARDEN IS GROWING MORE THAN JUST FOOD!",
    description: "Short subheading of the article.",
    body: "From food security to leadership – discover how one garden became a classroom, a community hub, and a catalyst for change. How a School Garden became a hub for Learning, Leadership, and Community. Watching others harvest, cook, or take photos in the greenery often sparks envy. But student leaders are clear: commitment matters. \"If they like the benefits,\" explained the teacher, \"I don't include them. I work with those who genuinely want to learn.\" That spirit has sparked talk of non-monetary incentives, certificates, testimonials, and public recognition to celebrate student dedication and support their future paths. Of course, challenges remain: unreliable irrigation, limited time, and the...",
    image: "https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338480/image_2049_qnlzfl.png",
    lastUpdated: new Date().toISOString()
  };

  const { 
    publishedContent, 
    setPublishedContent, 
    saveContentToFirebase, 
    savedContents,
    saveContentVersion,
    isLoading 
  } = useContent();
  const [content, setContent] = useState(publishedContent);
  const [isEditing, setIsEditing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [editorHtml, setEditorHtml] = useState(initialContent.body);

  // Function to format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Just now";
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "Just now";
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    
    return date.toLocaleDateString();
  };

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  // Quill editor formats configuration
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'blockquote', 'code-block',
    'list',
    'link', 'image', 'video'
  ];

  // Scroll refs and state for left panel
  const leftContentRef = useRef(null);
  const leftThumbRef = useRef(null);
  const [leftScrollTop, setLeftScrollTop] = useState(0);
  const [leftThumbHeight, setLeftThumbHeight] = useState(40);
  const [isLeftDragging, setIsLeftDragging] = useState(false);
  const [leftStartY, setLeftStartY] = useState(0);
  const [leftStartScrollTop, setLeftStartScrollTop] = useState(0);

  // Scroll refs and state for right panel
  const rightContentRef = useRef(null);
  const rightThumbRef = useRef(null);
  const [rightScrollTop, setRightScrollTop] = useState(0);
  const [rightThumbHeight, setRightThumbHeight] = useState(40);
  const [isRightDragging, setIsRightDragging] = useState(false);
  const [rightStartY, setRightStartY] = useState(0);
  const [rightStartScrollTop, setRightStartScrollTop] = useState(0);

  // Handle text changes
  const handleTextChange = (field, value) => {
    setContent(prev => ({ 
      ...prev, 
      [field]: value,
      lastUpdated: new Date().toISOString() // Update timestamp on any change
    }));
  };

  // Handle editor content change
  const handleEditorChange = (html) => {
    setContent(prev => ({
      ...prev,
      body: html,
      lastUpdated: new Date().toISOString()
    }));
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const result = await uploadToCloudinary(file, { folder: 'banner_images' });
      setContent(prev => ({ 
        ...prev, 
        image: result.secure_url, 
        isVideo: false,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
    }
  };

  // Video upload handler
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const result = await uploadToCloudinary(file, { folder: 'banner_videos' });
      setContent(prev => ({ 
        ...prev, 
        video: result.secure_url, 
        isVideo: true,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      console.error("Error uploading video:", error);
      alert("Failed to upload video. Please try again.");
    }
  };

  // Delete handler
  const handleDelete = () => {
    setContent({
      title: "",
      description: "",
      body: "",
      image: "",
      video: "",
      isVideo: false,
      lastUpdated: new Date().toISOString()
    });
    setIsEditing(true);
  };

  // Save handler
  // In your save handlers, make sure you're not modifying the content format
const handleSave = async () => {
  try {
    const contentToSave = {
      ...content,
      body: content.body, // preserve original formatting
      lastUpdated: new Date().toISOString()
    };
    await saveContentVersion(contentToSave);
    alert("Content saved successfully!");
  } catch (error) {
    console.error("Error saving content:", error);
    alert("Failed to save content. Please try again.");
  }
};
  // Publish handler
  const handlePublish = async () => {
    try {
      const contentToPublish = {
        ...content,
        lastUpdated: new Date().toISOString()
      };
      await saveContentToFirebase(contentToPublish);
      setPublishedContent(contentToPublish);
      setIsEditing(false);
      setIsPublished(true);
      alert("Content published successfully!");
    } catch (error) {
      console.error("Error publishing content:", error);
      alert("Failed to publish content. Please try again.");
    }
  };

  // Left panel scroll effect handlers
  useEffect(() => {
    const content = leftContentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setLeftThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [content.body]);

  const handleLeftScroll = () => {
    const content = leftContentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - leftThumbHeight;
    setLeftScrollTop(scrollRatio * trackHeight);
  };

  const handleLeftThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsLeftDragging(true);
    setLeftStartY(e.clientY);
    setLeftStartScrollTop(leftContentRef.current.scrollTop);

    document.addEventListener("mousemove", handleLeftThumbMouseMove);
    document.addEventListener("mouseup", handleLeftThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleLeftThumbMouseMove = (e) => {
    if (!isLeftDragging) return;

    const deltaY = e.clientY - leftStartY;
    const content = leftContentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - leftThumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, leftStartScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleLeftThumbMouseUp = () => {
    setIsLeftDragging(false);
    document.removeEventListener("mousemove", handleLeftThumbMouseMove);
    document.removeEventListener("mouseup", handleLeftThumbMouseUp);
    document.body.style.cursor = "";
  };

  const handleLeftTrackClick = (e) => {
    const content = leftContentRef.current;
    if (!content || e.target === leftThumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = leftThumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - leftThumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  // Right panel scroll effect handlers
  useEffect(() => {
    const content = rightContentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setRightThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [savedContents]);

  const handleRightScroll = () => {
    const content = rightContentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - rightThumbHeight;
    setRightScrollTop(scrollRatio * trackHeight);
  };

  const handleRightThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsRightDragging(true);
    setRightStartY(e.clientY);
    setRightStartScrollTop(rightContentRef.current.scrollTop);

    document.addEventListener("mousemove", handleRightThumbMouseMove);
    document.addEventListener("mouseup", handleRightThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleRightThumbMouseMove = (e) => {
    if (!isRightDragging) return;

    const deltaY = e.clientY - rightStartY;
    const content = rightContentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - rightThumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, rightStartScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleRightThumbMouseUp = () => {
    setIsRightDragging(false);
    document.removeEventListener("mousemove", handleRightThumbMouseMove);
    document.removeEventListener("mouseup", handleRightThumbMouseUp);
    document.body.style.cursor = "";
  };
  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const isVideo = file.type.includes('video');
    
    try {
      const result = await uploadToCloudinary(file, { 
        folder: isVideo ? 'banner_videos' : 'banner_images' 
      });
      
      setContent(prev => ({ 
        ...prev, 
        [isVideo ? 'video' : 'image']: result.secure_url, 
        isVideo,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      console.error("Error uploading media:", error);
      alert(`Failed to upload ${isVideo ? 'video' : 'image'}. Please try again.`);
    }
  };
  const handleRightTrackClick = (e) => {
    const content = rightContentRef.current;
    if (!content || e.target === rightThumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = rightThumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - rightThumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  return (
    <div className="bg-[#FAFBFB]">
      <div className="md:pt-4 pt-1">
        <Header/>
        <div className="flex flex-col md:min-h-screen">
          <div className="flex flex-grow">
            <div>
              <AdminSideBar />
            </div>
            <div className="px-1 md:px-0 mt-6 font-Inter ">
              {/* Main Heading and Search Input */}
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0"
              >
                <h1 className="font-semibold text-[24px] lg:text-[24px]">
                  Top Banner
                </h1>
                <div className="relative w-full lg:w-auto">
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full lg:w-[622px] h-[40px] lg:h-[57px] text-[#919291] text-sm lg:text-[15px] font-medium p-3 pr-10 border-[3px] border-[#D9D9D9] rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                  <HiSearch
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                    size={20}
                  />
                </div>
              </motion.div>

              {/* Publish Button */}
              <div className="flex justify-end mt-4">
                <button 
                  className="text-[14px] font-bold bg-[#114918] text-white rounded-md w-[155px] h-[26px] lg:w-[155px] lg:h-[26px]"
                  onClick={handlePublish}
                >
                  {isPublished ? "Published" : "Publish"}
                </button>
              </div>

              <div className="flex p-4 gap-4 bg-white w-full">
                {/* Left Panel with Custom Scroll */}
                <div className="w-[644px]">
                  <div className="relative w-[644px] h-[350px]">
                  {content.isVideo && content.video ? (
                    <video 
                      controls
                      className="w-full h-full object-cover rounded-md"
                    >
                      <source src={content.video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : content.image ? (
                    <img
                      src={content.image}
                      alt="Main"
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center rounded-md">
                      <span className="text-white">Add media</span>
                    </div>
                  )}
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md text-[12px]">
                      <label className="cursor-pointer flex items-center gap-1">
                        <span className="text-white">Add Media</span>
                        <img
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338638/Add_square_duotone_lzmlfq.png"
                          alt="Add Icon"
                          className="w-[46px] h-[46px]"
                        />
                        <input 
                          type="file" 
                          accept="image/*,video/*"  
                          onChange={handleMediaUpload}  
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Title and metadata section */}
                  <div className="mt-2">
                    <div className="flex justify-between items-center">
                      {isEditing ? (
                        <input
                          type="text"
                          value={content.title}
                          onChange={(e) => handleTextChange('title', e.target.value)}
                          placeholder="Write your heading..."
                          className="text-[20px] font-bold w-full bg-gray-100 p-1"
                        />
                      ) : (
                        <h2 className="text-[20px] font-bold">
                          {content.title || "Write your heading..."}
                        </h2>
                      )}
                      <button onClick={() => setIsEditing(!isEditing)}>
                        <img
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338468/Edit_fill_touk5a.png"
                          alt="Edit"
                          className="w-[22px] h-[22px]"
                        />
                      </button>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-[#155528] text-[10px] font-normal">
                      <span>{formatRelativeTime(content.lastUpdated)}</span>
                      {content.lastUpdated && (
                        <img
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754339200/Time_progress_light_dhuwuz.png"
                          alt="Time"
                          className="w-[10px] h-[10px]"
                        />
                      )}
                    </div>
                  </div>
                 
                  {/* Left Panel Paragraph with Custom Scroll */}
                  <div className="mt-2 h-[200px]">
                    <div className="w-full h-5 flex justify-end">
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
                        alt="scroll-up"
                        className="md:w-4 md:h-4 h-2 w-2"
                        onClick={() => {
                          leftContentRef.current.scrollBy({
                            top: -50,
                            behavior: "smooth",
                          });
                        }}
                      />
                    </div>

                    <div className="custom-scroll-area">
                      <div
                        className="custom-scroll-content"
                        ref={leftContentRef}
                        onScroll={handleLeftScroll}
                      >
                        {isEditing ? (
                          <ReactQuill
                            value={content.body}
                            onChange={handleEditorChange}
                            modules={modules}
                            formats={formats}
                            placeholder="Write your content here..."
                            className="h-[200px] mb-8"
                          />
                        ) : (
                          <div 
  className="h-[200px] whitespace-pre-wrap"
  dangerouslySetInnerHTML={{ __html: content.body || "Write your content here..." }}
/>
                        )}
                      </div>
                      <div className="custom-scroll-track" onClick={handleLeftTrackClick}>
                        <div
                          ref={leftThumbRef}
                          className="custom-scroll-thumb"
                          style={{
                            height: `${leftThumbHeight}px`,
                            top: `${leftScrollTop}px`,
                            cursor: isLeftDragging ? "grabbing" : "grab",
                          }}
                          onMouseDown={handleLeftThumbMouseDown}
                        ></div>
                      </div>
                    </div>

                    <div className="w-full h-5 flex justify-end">
                      <img
                        src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
                        alt="scroll-down"
                        className="md:w-4 md:h-4 h-2 w-2"
                        onClick={() => {
                          leftContentRef.current.scrollBy({
                            top: 50,
                            behavior: "smooth",
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center gap-4 mt-20">
                    <button 
                      className="flex items-center gap-1 text-[12px] font-normal"
                      onClick={handleDelete}
                    >
                      <img
                        src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338477/Trash_light_ysrgmo.png"
                        alt="Delete"
                        className="w-[24px] h-[24px]"
                      />
                      Delete
                    </button>
                    <button 
                      className="flex items-center gap-1 text-[12px] font-normal"
                      onClick={handleSave}
                    >
                      <img
                        src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338468/Arhive_load_light_owin6m.png"
                        alt="Save"
                        className="w-[24px] h-[24px]"
                      />
                      Save
                    </button>
                    <button 
                      className="flex items-center gap-1 text-[12px] font-normal"
                      onClick={handlePublish}
                    >
                      <img
                        src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338468/Send_light_o4hcbx.png"
                        alt="Publish"
                        className="w-[24px] h-[24px]"
                      />
                      Publish
                    </button>
                  </div>
                </div>

                {/* Right Panel with Custom Scroll */}
                <div className="flex flex-col gap-4">
                  <div className="w-full h-5 flex justify-end">
                    <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
                      alt="scroll-up"
                      className="md:w-4 md:h-4 h-2 w-2"
                      onClick={() => {
                        rightContentRef.current.scrollBy({
                          top: -50,
                          behavior: "smooth",
                        });
                      }}
                    />
                  </div>

                  <div className="custom-scroll-area">
                    <div
                      className="custom-scroll-content"
                      ref={rightContentRef}
                      onScroll={handleRightScroll}
                    >
                      {savedContents.map((item, index) => (
                        <div key={index} className="w-[335px] mb-4">
                          <div className="relative w-[335px] h-[190px]">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt="Thumbnail"
                                className="w-full h-full object-cover rounded-md"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-md">
                                <span className="text-gray-500">No Image</span>
                              </div>
                            )}
                            <button 
                              className="absolute top-1/2 left-14 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded text-white text-[14px]"
                              onClick={() => {
                                setContent(item);
                                setIsEditing(true);
                              }}
                            >
                              <img
                                src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1754338468/Sign_out_squre_ioda0g.png"
                                alt="Move to Edit"
                                className="w-[16px] h-[16px]"
                              />
                              Move to edit
                            </button>
                          </div>
                          <h3 className="text-[20px] font-bold mt-1 truncate">
                            {item.title || "No title"}
                          </h3>
                          <p className="text-[16px] font-normal whitespace-pre-wrap">
  {item.body ? 
    item.body.replace(/<[^>]*>/g, ' ') // Remove HTML tags but preserve spacing
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .substring(0, 50) + '...' : 
    "No content"
  }
</p>
                          <p className="text-[10px] text-[#155528]">
                            {formatRelativeTime(item.lastUpdated)}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="custom-scroll-track" onClick={handleRightTrackClick}>
                      <div
                        ref={rightThumbRef}
                        className="custom-scroll-thumb"
                        style={{
                          height: `${rightThumbHeight}px`,
                          top: `${rightScrollTop}px`,
                          cursor: isRightDragging ? "grabbing" : "grab",
                        }}
                        onMouseDown={handleRightThumbMouseDown}
                      ></div>
                    </div>
                  </div>

                  <div className="w-full h-5 flex justify-end">
                    <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
                      alt="scroll-down"
                      className="md:w-4 md:h-4 h-2 w-2"
                      onClick={() => {
                        rightContentRef.current.scrollBy({
                          top: 50,
                          behavior: "smooth",
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <Footer />
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 800px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 1px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
          .custom-scroll-content {
            flex: 1;
            overflow-y: scroll;
            scrollbar-width: none;
            -ms-overflow-style: none;
            padding-right: 0px;
          }
        }
      `}</style>
    </div>
  );
};

export default TopBannerMainPage;